import React, { useEffect, useState } from "react";
import { Share, Plus, X } from "lucide-react";

/**
 * IOSInstallPrompt — two-stage iOS install nudge.
 *
 * iOS Safari does NOT expose a programmatic install API (no
 * `beforeinstallprompt` equivalent). The only way to install a PWA on iOS is
 * the user tapping Share → Add to Home Screen themselves. So this component
 * goes for the easiest possible UX:
 *
 *   Stage 1 — "Install TalaDraw on your iPhone?  [Not now] [Yes, install]"
 *   Stage 2 — if Yes: morph to show the Share → Add-to-Home-Screen steps.
 *   If Not now: dismissed for 14 days, no further nags.
 *
 * Visibility rules (all must be true):
 *  - iOS device (iPhone/iPad/iPod, incl. iPadOS lying as Mac via maxTouchPoints)
 *  - Safari (not in-app browser like Chrome iOS / Facebook / Instagram)
 *  - NOT already installed (`navigator.standalone === true`)
 *  - User hasn't dismissed it in the last 14 days
 *
 * Show is delayed ~3 s after mount so it doesn't clobber first paint.
 */

const DISMISS_KEY = "taladraw_ios_install_dismissed_at";
const DISMISS_WINDOW_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isIosSafari() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";

  const isIPhone = /iPhone|iPod/.test(ua);
  const isIPad =
    /iPad/.test(ua) ||
    (platform === "MacIntel" && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1);

  if (!isIPhone && !isIPad) return false;

  // Reject in-app browsers — they can't install PWAs on iOS at all.
  if (/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|FBAN|FBAV|Instagram|Line|MicroMessenger/.test(ua)) {
    return false;
  }
  return /Safari/.test(ua);
}

function isInStandaloneMode() {
  if (typeof window === "undefined") return false;
  if (window.navigator && window.navigator.standalone === true) return true;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  return false;
}

export default function IOSInstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    if (!isIosSafari()) return;
    if (isInStandaloneMode()) return;

    try {
      const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_WINDOW_MS) return;
    } catch {
      /* localStorage blocked → still show */
    }

    const t = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch { /* ignore */ }
    setVisible(false);
    setShowInstructions(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[9999] px-3 pb-[max(env(safe-area-inset-bottom),12px)] pointer-events-none"
      data-testid="ios-install-prompt"
    >
      <div className="mx-auto max-w-md rounded-2xl border border-zinc-800 bg-zinc-950/95 backdrop-blur shadow-2xl pointer-events-auto animate-in slide-in-from-bottom duration-500">
        <div className="flex items-start gap-3 p-4">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-[#00FF94]/15 border border-[#00FF94]/30 flex items-center justify-center">
            <img src="/logo.png" alt="TalaDraw" className="w-6 h-6" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-semibold leading-snug">
              {showInstructions ? "Here's how to add it" : "Install TalaDraw on your iPhone?"}
            </div>
            <div className="text-zinc-400 text-xs mt-1 leading-relaxed">
              {showInstructions
                ? "Two quick taps in Safari and you're done."
                : "Get full-screen access, faster launch, and ATM alerts."}
            </div>

            {showInstructions && (
              <ol className="mt-3 space-y-2 text-xs text-zinc-300 leading-relaxed">
                <li>
                  <span className="text-[#00FF94] font-bold mr-1.5">1.</span>
                  Tap the{" "}
                  <Share className="inline-block w-3.5 h-3.5 -mt-0.5 text-[#00FF94]" strokeWidth={2.5} />
                  {" "}Share button in Safari&apos;s toolbar
                </li>
                <li>
                  <span className="text-[#00FF94] font-bold mr-1.5">2.</span>
                  Choose{" "}
                  <Plus className="inline-block w-3.5 h-3.5 -mt-0.5 text-[#00FF94]" strokeWidth={2.5} />
                  {" "}<span className="font-semibold text-white">Add to Home Screen</span>
                </li>
              </ol>
            )}

            <div className="mt-3 flex items-center gap-2">
              {showInstructions ? (
                <button
                  type="button"
                  onClick={dismiss}
                  className="flex-1 rounded-lg bg-[#00FF94] text-black text-xs font-semibold py-2 hover:bg-[#00FF94]/90 transition-colors"
                  data-testid="ios-install-prompt-got-it"
                >
                  Got it
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={dismiss}
                    className="flex-1 rounded-lg border border-zinc-700 text-zinc-300 text-xs font-medium py-2 hover:bg-zinc-800 transition-colors"
                    data-testid="ios-install-prompt-no"
                  >
                    Not now
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowInstructions(true)}
                    className="flex-1 rounded-lg bg-[#00FF94] text-black text-xs font-semibold py-2 hover:bg-[#00FF94]/90 transition-colors"
                    data-testid="ios-install-prompt-yes"
                  >
                    Yes, install
                  </button>
                </>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={dismiss}
            className="shrink-0 p-1 rounded-md hover:bg-zinc-800 text-zinc-500 hover:text-zinc-200 transition-colors"
            aria-label="Dismiss install prompt"
            data-testid="ios-install-prompt-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
